import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiError } from '../App';
import {
  ArrowLeft, Layers, Loader2, MapPin, Plus, Save, Trash2, Pencil,
  Users, Ruler, ListPlus, FileText, X, Link2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';

const fmtNum = (v) => (v == null ? '—' : new Intl.NumberFormat('en-IN').format(v));
const fmtArea = (v) => (v == null ? '—' : new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(v));
const fmtMoney = (v) => (v == null || v === 0 ? '—' : `₹${new Intl.NumberFormat('en-IN').format(Math.round(v))}`);

export default function ProjectDetail({ user, setUser }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [byMember, setByMember] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [linkPropsOpen, setLinkPropsOpen] = useState(false);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [proj, bym, fam] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/by-member`),
        api.get('/family-members').catch(() => ({ data: [] })),
      ]);
      setProject(proj.data);
      setByMember(bym.data.groups || []);
      setMembers(Array.isArray(fam.data) ? fam.data : (fam.data.members || []));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !project) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  const totals = project.totals || {};

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-5" data-testid="project-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <button
              onClick={() => navigate('/projects')}
              className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 mb-1"
              data-testid="back-to-projects"
            >
              <ArrowLeft className="w-4 h-4" /> All projects
            </button>
            <h1 className="text-2xl font-bold text-[#1a2e44] flex items-center gap-2">
              <Layers className="w-6 h-6 text-blue-600" />
              {project.name}
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100 capitalize ml-1">
                {project.project_type}
              </span>
            </h1>
            {project.location && (
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {project.location}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditProjectOpen(true)} data-testid="edit-project-btn">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLinkPropsOpen(true)} data-testid="link-props-btn">
              <Link2 className="w-4 h-4 mr-1.5" /> Link properties
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-add-btn">
              <ListPlus className="w-4 h-4 mr-1.5" /> Bulk add blocks
            </Button>
            <Button size="sm" onClick={() => setAddBlockOpen(true)} data-testid="add-block-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
              <Plus className="w-4 h-4 mr-1.5" /> Add block
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Blocks" value={fmtNum(totals.block_count)} color="text-[#1a2e44]" />
          <Tile label="Total Sq.Yds" value={fmtArea(totals.total_area_sq_yds)} color="text-blue-600" />
          <Tile label="Total Sq.Ft" value={fmtArea(totals.total_area_sq_ft)} color="text-emerald-600" />
          <Tile label="Total Value" value={fmtMoney(totals.total_value)} color="text-purple-600" />
        </div>

        {project.description && (
          <div className="bg-blue-50/40 border border-blue-100 rounded-lg px-4 py-3 text-sm text-gray-700 leading-relaxed">
            {project.description}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="blocks" className="w-full">
          <TabsList data-testid="project-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="blocks" data-testid="tab-blocks">Blocks ({totals.block_count || 0})</TabsTrigger>
            <TabsTrigger value="by-member" data-testid="tab-by-member">By Member ({byMember.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-4">
            <OverviewTab project={project} />
          </TabsContent>
          <TabsContent value="blocks" className="pt-4">
            <BlocksTab project={project} members={members} onChange={load} />
          </TabsContent>
          <TabsContent value="by-member" className="pt-4">
            <ByMemberTab groups={byMember} />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <EditProjectDialog
          open={editProjectOpen}
          onClose={() => setEditProjectOpen(false)}
          project={project}
          onSaved={load}
        />
        <LinkPropertiesDialog
          open={linkPropsOpen}
          onClose={() => setLinkPropsOpen(false)}
          project={project}
          onSaved={load}
        />
        <BlockDialog
          open={addBlockOpen}
          onClose={() => setAddBlockOpen(false)}
          project={project}
          members={members}
          onSaved={load}
        />
        <BulkAddDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          project={project}
          members={members}
          onSaved={load}
        />
      </div>
    </Layout>
  );
}

const Tile = ({ label, value, color }) => (
  <div className="bg-white rounded-lg border border-gray-100 px-4 py-3 shadow-sm">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-xl font-semibold ${color}`}>{value}</p>
  </div>
);

// ==================== OVERVIEW ====================
const OverviewTab = ({ project }) => {
  const sources = project.source_properties || [];
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-500" /> Linked source properties ({sources.length})
        </h3>
        {sources.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No source properties linked. Use "Link properties" above to attach existing master properties (e.g., J-3, J-4).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2 font-medium">S.No</th>
                  <th className="text-left py-2 px-2 font-medium">RS No</th>
                  <th className="text-left py-2 px-2 font-medium">Area</th>
                  <th className="text-left py-2 px-2 font-medium">Owner</th>
                  <th className="text-right py-2 px-2 font-medium">Extent</th>
                  <th className="text-left py-2 px-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium">{s.serial_no || '—'}</td>
                    <td className="py-2 px-2 text-gray-600">{s.rs_no || '—'}</td>
                    <td className="py-2 px-2 text-gray-600">{s.area || '—'}</td>
                    <td className="py-2 px-2 text-gray-600">{s.owner_name || '—'}</td>
                    <td className="py-2 px-2 text-right text-gray-600">
                      {s.extent != null ? `${s.extent}${s.units ? ' ' + s.units : ''}` : '—'}
                    </td>
                    <td className="py-2 px-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] capitalize bg-gray-100 text-gray-600">
                        {s.status || 'active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Project info</h3>
          <dl className="text-sm space-y-2">
            <Row k="Type" v={project.project_type} />
            <Row k="Location" v={project.location} />
            <Row k="Default rate / Sq.Yd" v={project.default_rate_per_sqyd ? fmtMoney(project.default_rate_per_sqyd) : 'Not set'} />
            <Row k="Notes" v={project.notes || '—'} />
          </dl>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Aggregate totals</h3>
          <dl className="text-sm space-y-2">
            <Row k="Total blocks" v={fmtNum(project.totals?.block_count)} />
            <Row k="Total area (Sq.Yds)" v={fmtArea(project.totals?.total_area_sq_yds)} />
            <Row k="Total area (Sq.Ft)" v={fmtArea(project.totals?.total_area_sq_ft)} />
            <Row k="Total value" v={fmtMoney(project.totals?.total_value)} />
          </dl>
        </div>
      </div>
    </div>
  );
};
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-gray-50 last:border-0">
    <dt className="text-gray-500">{k}</dt>
    <dd className="text-gray-800 font-medium text-right">{v || '—'}</dd>
  </div>
);

// ==================== BLOCKS TAB ====================
const BlocksTab = ({ project, members, onChange }) => {
  const [filter, setFilter] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const blocks = project.blocks || [];
  const schedules = useMemo(
    () => Array.from(new Set(blocks.map(b => b.schedule_label).filter(Boolean))).sort(),
    [blocks]
  );
  const memberOptions = useMemo(() => {
    const seen = new Map();
    blocks.forEach(b => {
      const key = b.member_id || b.member_name || 'Unassigned';
      if (!seen.has(key)) seen.set(key, b.member_name || 'Unassigned');
    });
    return Array.from(seen, ([k, v]) => ({ key: k, label: v }));
  }, [blocks]);

  const filtered = blocks.filter(b => {
    if (filter && !(`${b.block_no || ''} ${b.member_name || ''}`).toLowerCase().includes(filter.toLowerCase())) return false;
    if (scheduleFilter !== 'all' && b.schedule_label !== scheduleFilter) return false;
    if (memberFilter !== 'all' && (b.member_id || b.member_name || 'Unassigned') !== memberFilter) return false;
    return true;
  });

  const onDelete = async (b) => {
    if (!window.confirm(`Delete block ${b.block_no || '(open land)'}?`)) return;
    try {
      await api.delete(`/projects/${project.id}/blocks/${b.id}`);
      toast.success('Deleted');
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="p-3 border-b border-gray-100 flex flex-wrap gap-2">
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search block no / member…"
          data-testid="blocks-search"
          className="flex-1 min-w-[200px] px-3 py-1.5 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <select
          value={scheduleFilter}
          onChange={e => setScheduleFilter(e.target.value)}
          data-testid="schedule-filter"
          className="px-3 py-1.5 rounded-md border border-gray-300 text-sm bg-white"
        >
          <option value="all">All Schedules</option>
          {schedules.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={memberFilter}
          onChange={e => setMemberFilter(e.target.value)}
          data-testid="member-filter"
          className="px-3 py-1.5 rounded-md border border-gray-300 text-sm bg-white"
        >
          <option value="all">All Members</option>
          {memberOptions.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 italic p-8 text-center">No blocks match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="blocks-table">
            <thead className="text-xs text-gray-500 bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Schedule</th>
                <th className="text-left py-2 px-3 font-medium">Block / Plot</th>
                <th className="text-right py-2 px-3 font-medium">Sq.Yds</th>
                <th className="text-right py-2 px-3 font-medium">Sq.Ft</th>
                <th className="text-left py-2 px-3 font-medium">Member</th>
                <th className="text-right py-2 px-3 font-medium">Value</th>
                <th className="text-left py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-3 text-gray-600">
                    <span className="px-2 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-100">
                      {b.schedule_label || '—'}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-medium">
                    {b.is_open_land ? (
                      <span className="text-amber-700 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Open land
                      </span>
                    ) : (b.block_no || '—')}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtArea(b.area_sq_yds)}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-500">{fmtArea(b.area_sq_ft)}</td>
                  <td className="py-2 px-3 text-gray-700">{b.member_name || <span className="italic text-gray-400">Unassigned</span>}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtMoney(b.computed_value)}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] capitalize ${
                      b.status === 'sold' ? 'bg-gray-100 text-gray-600'
                      : b.status === 'disputed' ? 'bg-amber-100 text-amber-700'
                      : b.status === 'unsold' ? 'bg-purple-100 text-purple-700'
                      : 'bg-emerald-100 text-emerald-700'
                    }`}>{b.status || 'allotted'}</span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(b)} className="text-gray-400 hover:text-blue-600 p-1" data-testid={`edit-block-${b.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => onDelete(b)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`delete-block-${b.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BlockDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        project={project}
        members={members}
        block={editing}
        onSaved={() => { setEditing(null); onChange?.(); }}
      />
    </div>
  );
};

// ==================== BY MEMBER TAB ====================
const ByMemberTab = ({ groups }) => {
  if (!groups.length) {
    return <p className="text-sm text-gray-500 italic p-8 text-center bg-white rounded-xl border border-gray-200">No blocks yet.</p>;
  }
  return (
    <div className="space-y-4" data-testid="by-member-list">
      {groups.map(g => (
        <div key={g.member_id || g.member_name} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-[#1a2e44] flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" /> {g.member_name}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{g.block_count} blocks</p>
            </div>
            <div className="flex gap-6 text-sm">
              <div className="text-right">
                <p className="text-[10px] uppercase text-gray-400">Total Sq.Yds</p>
                <p className="font-semibold text-blue-600 tabular-nums">{fmtArea(g.total_area_sq_yds)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase text-gray-400">Total Sq.Ft</p>
                <p className="font-semibold text-emerald-600 tabular-nums">{fmtArea(g.total_area_sq_ft)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase text-gray-400">Total Value</p>
                <p className="font-semibold text-purple-600 tabular-nums">{fmtMoney(g.total_value)}</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500">
                <tr>
                  <th className="text-left py-2 px-4 font-medium">Schedule</th>
                  <th className="text-left py-2 px-4 font-medium">Block</th>
                  <th className="text-right py-2 px-4 font-medium">Sq.Yds</th>
                  <th className="text-right py-2 px-4 font-medium">Sq.Ft</th>
                  <th className="text-right py-2 px-4 font-medium">Value</th>
                  <th className="text-left py-2 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {g.blocks.map((b, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="py-1.5 px-4 text-gray-500 text-xs">{b.schedule_label || '—'}</td>
                    <td className="py-1.5 px-4 font-medium">
                      {b.is_open_land ? <span className="text-amber-700">Open land</span> : (b.block_no || '—')}
                    </td>
                    <td className="py-1.5 px-4 text-right tabular-nums">{fmtArea(b.area_sq_yds)}</td>
                    <td className="py-1.5 px-4 text-right tabular-nums text-gray-500">{fmtArea(b.area_sq_ft)}</td>
                    <td className="py-1.5 px-4 text-right tabular-nums">{fmtMoney(b.value)}</td>
                    <td className="py-1.5 px-4 text-xs capitalize text-gray-600">{b.status || 'allotted'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==================== EDIT PROJECT DIALOG ====================
const EditProjectDialog = ({ open, onClose, project, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && project) {
      setForm({
        name: project.name || '',
        description: project.description || '',
        project_type: project.project_type || 'plot',
        location: project.location || '',
        default_rate_per_sqyd: project.default_rate_per_sqyd ?? '',
        notes: project.notes || '',
      });
    }
  }, [open, project]);

  const submit = async () => {
    try {
      setSaving(true);
      const payload = { ...form };
      payload.default_rate_per_sqyd = payload.default_rate_per_sqyd === '' ? null : parseFloat(payload.default_rate_per_sqyd);
      await api.put(`/projects/${project.id}`, payload);
      toast.success('Project updated');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-project-dialog">
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Field label="Name">
            <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Type">
            <select value={form.project_type || 'plot'} onChange={e => setForm({ ...form, project_type: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white">
              <option value="plot">Plot Layout</option>
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="mixed">Mixed</option>
            </select>
          </Field>
          <Field label="Location">
            <input value={form.location || ''} onChange={e => setForm({ ...form, location: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Default rate / Sq.Yd">
            <input type="number" value={form.default_rate_per_sqyd ?? ''}
              onChange={e => setForm({ ...form, default_rate_per_sqyd: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Description">
            <textarea rows={3} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-edit-project-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== LINK PROPERTIES DIALOG ====================
const LinkPropertiesDialog = ({ open, onClose, project, onSaved }) => {
  const [allProps, setAllProps] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(project.source_property_ids || []));
    api.get('/properties').then(r => setAllProps(r.data.properties || [])).catch(e => toast.error(formatApiError(e)));
  }, [open, project]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async () => {
    try {
      setSaving(true);
      await api.put(`/projects/${project.id}`, { source_property_ids: Array.from(selected) });
      toast.success('Linked properties updated');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const filtered = allProps.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.serial_no || '').toLowerCase().includes(q)
      || (p.rs_no || '').toLowerCase().includes(q)
      || (p.area || '').toLowerCase().includes(q)
      || (p.owner_name || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="link-properties-dialog">
        <DialogHeader>
          <DialogTitle>Link source properties</DialogTitle>
          <DialogDescription>
            Select master properties that consolidate into this project.
          </DialogDescription>
        </DialogHeader>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by S.No, RS No, area, owner…"
          className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm mb-3"
        />
        <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 italic p-4">No properties.</p>
          ) : filtered.map(p => (
            <label key={p.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-0 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
              <div className="flex-1 text-sm">
                <span className="font-medium">{p.serial_no || '—'}</span>
                <span className="text-gray-500"> · {p.rs_no || '—'} · {p.area || '—'} · {p.owner_name || '—'}</span>
              </div>
              <span className="text-xs text-gray-400">{p.extent ?? '—'} {p.units || ''}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <span className="text-xs text-gray-500 mr-auto">{selected.size} selected</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-link-props-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== ADD/EDIT BLOCK DIALOG ====================
const BlockDialog = ({ open, onClose, project, members, block, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!block;

  useEffect(() => {
    if (!open) return;
    setForm({
      block_no: block?.block_no || '',
      schedule_label: block?.schedule_label || '',
      area_sq_yds: block?.area_sq_yds ?? '',
      member_id: block?.member_id || '',
      member_name: block?.member_name || '',
      rate_per_sqyd: block?.rate_per_sqyd ?? '',
      proportionate_value: block?.proportionate_value ?? '',
      status: block?.status || 'allotted',
      is_open_land: !!block?.is_open_land,
      bounded_by: block?.bounded_by || '',
      notes: block?.notes || '',
    });
  }, [open, block]);

  const submit = async () => {
    try {
      setSaving(true);
      const payload = {
        block_no: form.block_no || null,
        schedule_label: form.schedule_label || null,
        area_sq_yds: form.area_sq_yds === '' ? 0 : parseFloat(form.area_sq_yds),
        member_id: form.member_id || null,
        member_name: form.member_name || null,
        rate_per_sqyd: form.rate_per_sqyd === '' ? null : parseFloat(form.rate_per_sqyd),
        proportionate_value: form.proportionate_value === '' ? null : parseFloat(form.proportionate_value),
        status: form.status,
        is_open_land: !!form.is_open_land,
        bounded_by: form.bounded_by || null,
        notes: form.notes || null,
      };
      // strip nulls for create (BlockCreate has defaults)
      if (isEdit) {
        await api.put(`/projects/${project.id}/blocks/${block.id}`,
          Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== null && v !== undefined))
        );
      } else {
        await api.post(`/projects/${project.id}/blocks`, payload);
      }
      toast.success(isEdit ? 'Block updated' : 'Block added');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit block' : 'Add block'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Block / Plot No">
            <input value={form.block_no || ''} onChange={e => setForm({ ...form, block_no: e.target.value })}
              data-testid="block-no-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Schedule">
            <input value={form.schedule_label || ''} onChange={e => setForm({ ...form, schedule_label: e.target.value })}
              placeholder="Schedule-I"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Area (Sq.Yds) *">
            <input type="number" value={form.area_sq_yds ?? ''} onChange={e => setForm({ ...form, area_sq_yds: e.target.value })}
              data-testid="block-area-input"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Status">
            <select value={form.status || 'allotted'} onChange={e => setForm({ ...form, status: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white">
              <option value="allotted">Allotted</option>
              <option value="sold">Sold</option>
              <option value="disputed">Disputed</option>
              <option value="unsold">Unsold</option>
            </select>
          </Field>
          <Field label="Family Member">
            <select
              value={form.member_id || ''}
              onChange={e => {
                const m = members.find(x => x.id === e.target.value);
                setForm({ ...form, member_id: e.target.value, member_name: m?.name || form.member_name });
              }}
              data-testid="block-member-select"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white"
            >
              <option value="">— Free text below —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Member name (free text)">
            <input value={form.member_name || ''} onChange={e => setForm({ ...form, member_name: e.target.value })}
              placeholder="e.g., Vallabhaneni Ramya Sri"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Rate / Sq.Yd (override)">
            <input type="number" value={form.rate_per_sqyd ?? ''} onChange={e => setForm({ ...form, rate_per_sqyd: e.target.value })}
              placeholder={project.default_rate_per_sqyd ? `Default ${project.default_rate_per_sqyd}` : 'No default'}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Explicit value (override)">
            <input type="number" value={form.proportionate_value ?? ''} onChange={e => setForm({ ...form, proportionate_value: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_open_land} onChange={e => setForm({ ...form, is_open_land: e.target.checked })} />
            <span>This is open land (no plot number)</span>
          </label>
          {form.is_open_land && (
            <Field label="Bounded by" className="col-span-2">
              <textarea rows={2} value={form.bounded_by || ''} onChange={e => setForm({ ...form, bounded_by: e.target.value })}
                className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-block-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {isEdit ? 'Save' : 'Add block'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== BULK ADD DIALOG ====================
const BulkAddDialog = ({ open, onClose, project, members, onSaved }) => {
  const [scheduleLabel, setScheduleLabel] = useState('');
  const [memberId, setMemberId] = useState('');
  const [rate, setRate] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setScheduleLabel(''); setMemberId(''); setRate(''); setText('');
    }
  }, [open]);

  const parsed = useMemo(() => {
    return text.split('\n').map(line => line.trim()).filter(Boolean).map(line => {
      // accept "163 284.1" or "163, 284.1" or "163\t284.1"
      const parts = line.split(/[\s,\t]+/).filter(Boolean);
      if (parts.length < 2) return null;
      const block_no = parts[0];
      const area_sq_yds = parseFloat(parts[1]);
      if (isNaN(area_sq_yds)) return null;
      return { block_no, area_sq_yds };
    }).filter(Boolean);
  }, [text]);

  const submit = async () => {
    if (parsed.length === 0) { toast.error('No valid rows parsed'); return; }
    try {
      setSaving(true);
      const m = members.find(x => x.id === memberId);
      await api.post(`/projects/${project.id}/blocks/bulk`, {
        schedule_label: scheduleLabel || null,
        member_id: memberId || null,
        member_name: m?.name || null,
        rate_per_sqyd: rate ? parseFloat(rate) : null,
        blocks: parsed,
      });
      toast.success(`Added ${parsed.length} blocks`);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="bulk-add-dialog">
        <DialogHeader>
          <DialogTitle>Bulk add blocks</DialogTitle>
          <DialogDescription>
            Paste rows as "block_no, area_sq_yds" — one per line. Schedule, member &amp; rate apply to all rows.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-2">
          <Field label="Schedule">
            <input value={scheduleLabel} onChange={e => setScheduleLabel(e.target.value)}
              placeholder="Schedule-I"
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
          <Field label="Member">
            <select value={memberId} onChange={e => setMemberId(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white">
              <option value="">— None —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Rate / Sq.Yd">
            <input type="number" value={rate} onChange={e => setRate(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm" />
          </Field>
        </div>
        <Field label={`Rows (${parsed.length} valid)`}>
          <textarea
            rows={10} value={text} onChange={e => setText(e.target.value)}
            placeholder={`163 284.1\n164 240.9\n165 240.5`}
            data-testid="bulk-rows-input"
            className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono"
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || parsed.length === 0}
            data-testid="save-bulk-btn" className="bg-[#1a2e44] hover:bg-[#243a55]">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Add {parsed.length} blocks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);
